import { Dumbbell, Users, Wrench, HeartHandshake, ArrowUpRight } from "lucide-react";

export default function Features() {
  return (
    <>
      {/* The Gym section */}
      <section
        id="features"
        data-testid="features-section"
        className="relative py-24 lg:py-32 px-6 lg:px-10 bg-[#050505]"
      >
        <div className="max-w-7xl mx-auto">
          {/* Section label */}
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[#0055FF]" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-[#0055FF]">
              Inside JerseyFit Barbell
            </span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-end mb-14">
            <h2
              data-testid="features-heading"
              className="lg:col-span-8 font-heading font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.95]"
            >
              A Real Gym.
              <br />
              Built For <span className="text-[#0055FF]">Real Lifters.</span>
            </h2>
            <p className="lg:col-span-4 text-white/60 text-base sm:text-lg font-medium leading-relaxed">
              No fluff, no judgment, no overpriced smoothie bar. Just iron,
              platforms, and a community that shows up.
            </p>
          </div>

          {/* Bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large image card — Equipment */}
            <article
              data-testid="feature-card-equipment"
              className="md:col-span-7 md:row-span-2 relative border border-white/10 bg-[#0F0F11] overflow-hidden group min-h-[420px]"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_strength-club-3/artifacts/g621lqtv_Screenshot%202026-04-14%20161428.png"
                alt="Jerseyfit wall mural with barbells and racks"
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/70 to-transparent" />
              <div className="relative z-10 p-8 lg:p-10 h-full flex flex-col justify-end">
                <div className="flex items-center gap-2 mb-4">
                  <Wrench className="text-[#0055FF]" size={18} />
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#0055FF]">
                    01 · The Iron
                  </span>
                </div>
                <h3 className="font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight leading-[0.95] max-w-lg">
                  High Quality Equipment. Always Ready.
                </h3>
                <p className="mt-4 text-white/70 text-base font-medium max-w-lg leading-relaxed">
                  Calibrated plates, competition bars, power racks, platforms,
                  specialty bars, full dumbbell lineup, and every machine you
                  actually need. Maintained so you can train hard — every time.
                </p>
              </div>
            </article>

            {/* Training Styles card */}
            <article
              data-testid="feature-card-training"
              id="training"
              className="md:col-span-5 relative border border-white/10 bg-[#0F0F11] p-8 lg:p-10 group hover:border-[#0055FF]/60 transition-colors min-h-[200px]"
            >
              <Dumbbell className="text-[#0055FF]" size={28} />
              <div className="flex items-center gap-2 mt-6 mb-3">
                <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/50">
                  02 · All Styles
                </span>
              </div>
              <h3 className="font-heading font-extrabold text-2xl sm:text-3xl uppercase tracking-tight leading-tight">
                Every Lift.
                <br />
                Every Lifter.
              </h3>
              <p className="mt-4 text-white/65 text-sm font-medium leading-relaxed">
                Powerlifting, Olympic lifting, bodybuilding, strongman, general
                strength, first-time lifter — if you train, you belong. All
                styles welcome, all levels respected.
              </p>
              <ArrowUpRight
                className="absolute top-8 right-8 text-white/30 group-hover:text-[#0055FF] group-hover:translate-x-1 group-hover:-translate-y-1 transition-all"
                size={22}
              />
            </article>

            {/* Community card */}
            <article
              data-testid="feature-card-community"
              className="md:col-span-5 relative border border-white/10 p-8 lg:p-10 min-h-[200px] overflow-hidden group"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_strength-club-3/artifacts/cla48isp_Screenshot%202026-04-14%20161357.png"
                alt="Winged NJ mural at JerseyFit Barbell"
                className="absolute inset-0 w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity duration-700"
              />
              <div className="absolute inset-0 bg-[#0055FF]/80 mix-blend-multiply" />
              <div className="absolute inset-0 bg-gradient-to-tr from-[#0055FF] via-[#0055FF]/70 to-transparent" />
              <div className="absolute -right-10 -bottom-10 w-56 h-56 rounded-full bg-white/10 blur-2xl" />
              <div className="relative">
                <HeartHandshake className="text-white" size={28} />
                <div className="flex items-center gap-2 mt-6 mb-3">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
                    03 · Community
                  </span>
                </div>
                <h3 className="font-heading font-extrabold text-2xl sm:text-3xl uppercase tracking-tight text-white leading-tight">
                  You Walk In A Stranger. You Leave A Member.
                </h3>
                <p className="mt-4 text-white/90 text-sm font-medium leading-relaxed">
                  Our greatest asset is our community. We take great pride in
                  having the best members in the 618. From beginners to
                  experts, our community is saturated with elite people.
                </p>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* Split hero row — big action image */}
      <section
        data-testid="split-showcase-section"
        className="relative px-6 lg:px-10 pb-24 lg:pb-32 bg-[#050505]"
      >
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-6">
          <div
            data-testid="split-image-card"
            className="md:col-span-7 relative border border-white/10 overflow-hidden min-h-[380px]"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_strength-club-3/artifacts/e6ey06ae_Screenshot%202026-04-14%20161545.png"
              alt="The JerseyFit Barbell crew — group photo"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-[#050505]/20 to-transparent" />
            <div className="absolute top-6 left-6 right-6 flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-white/90 bg-black/60 backdrop-blur-md px-3 py-1.5 border border-white/20">
                The JerseyFit Crew
              </span>
              <span className="w-3 h-3 bg-[#0055FF]" />
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-white/80">
                Real members · Real team
              </div>
            </div>
          </div>

          <div
            data-testid="split-copy-card"
            className="md:col-span-5 relative border border-white/10 bg-[#0F0F11] p-10 lg:p-12 flex flex-col justify-between min-h-[380px]"
          >
            <div>
              <Users className="text-[#0055FF]" size={28} />
              <h3 className="mt-6 font-heading font-extrabold text-3xl sm:text-4xl uppercase tracking-tight leading-[0.95]">
                No Ego.
                <br />
                No Judgment.
                <br />
                Just Work.
              </h3>
              <p className="mt-6 text-white/65 text-base font-medium leading-relaxed">
                Whether you&apos;re chasing a first 225 bench or a 700 deadlift,
                you&apos;ll find the space, the iron, and the people to get
                there. This is what a gym is supposed to feel like.
              </p>
            </div>
            <div className="mt-10 grid grid-cols-3 gap-4 border-t border-white/10 pt-6">
              <div>
                <div className="font-heading font-black text-2xl text-white">
                  24/7
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                  Access
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-2xl text-[#0055FF]">
                  $50
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                  Flat Rate
                </div>
              </div>
              <div>
                <div className="font-heading font-black text-2xl text-white">
                  All
                </div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                  Lifters
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
